import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
  Circle as UnreadDot,
} from '@mui/icons-material';
import { getUnreadNotificationCount, getNotifications, markNotificationRead, markAllNotificationsRead } from '../api';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TYPE_COLORS = {
  entry_approved: '#10B981',
  entry_denied: '#EF4444',
  entry_submitted: '#3B82F6',
  entry_updated: '#F59E0B',
  pto_approved: '#10B981',
  pto_denied: '#EF4444',
  pto_requested: '#3B82F6',
  pto_balance_adjusted: '#F59E0B',
  payroll_processed: '#7C3AED',
  task_assigned: '#3B82F6',
  task_completed: '#10B981',
  task_pending: '#F59E0B',
  task_planned: '#3B82F6',
  task_reminder: '#F59E0B',
  w2_available: '#B45309',
  punch_replay_refused: '#EF4444',
};

export default function NotificationBell({ onNavigate }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      // Silently fail — bell just won't show badge
    }
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    try {
      const res = await getNotifications(30, 0);
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (notif) => {
    if (!notif.is_read) {
      try {
        await markNotificationRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
    // Navigate based on entity type. Task notifications share one entity
    // type ('task') but split across two screens depending on who it's
    // for, so check the specific notification type first.
    if (onNavigate) {
      const typeTabMap = {
        task_assigned: 'My Tasks',
        task_completed: 'Tasks',
        task_pending: 'Tasks',
        task_reminder: 'My Tasks',
        task_planned: 'Tasks',
        pto_balance_adjusted: 'Time Off',
      };
      const tabMap = {
        time_entry: 'Approvals',
        pto_request: 'Time Off',
        payroll_run: 'My Pay',
        tax_filing: 'My Pay',
      };
      const tab = typeTabMap[notif.type] || tabMap[notif.entity_type];
      if (tab) onNavigate(tab);
    }
    handleClose();
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="Notifications">
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0, maxHeight: 380, overflow: 'auto' }}>
            {notifications.map((notif) => (
              <ListItem key={notif.id} disablePadding>
                <ListItemButton
                  onClick={() => handleMarkRead(notif)}
                  sx={{ bgcolor: notif.is_read ? 'transparent' : 'action.hover' }}
                >
                  {!notif.is_read && (
                    <UnreadDot sx={{ fontSize: 10, color: TYPE_COLORS[notif.type] || 'primary.main', mr: 1.5, flexShrink: 0 }} />
                  )}
                  <ListItemText
                    primary={notif.title}
                    secondary={
                      <React.Fragment>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block', fontSize: '0.8rem' }}>
                          {notif.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" component="span">
                          {timeAgo(notif.created_at)}
                        </Typography>
                      </React.Fragment>
                    }
                    sx={{ ml: notif.is_read ? 3.5 : 0 }}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: notif.is_read ? 'normal' : 'bold' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
